





























































































































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";
// Enums
import { DataModules } from "@/store/modules/data";
// Types
import { IClient } from "@/models/clients";
import { IEmail } from "@/models/emails";
import { IState } from "@/store";
import LoggedUserMixin from "@/mixins/loggedUserMixin";
import { AxiosError } from "axios";

interface Form {
  email: string;
  has_login: boolean;
}

@Component({
  components: {
    "two-fa-switch": () =>
      import("@/components/app/global/auth/TwoFaSwitch.vue"),
    "client-email-select": () =>
      import("@/components/app/global/fields/clientEmailSelect.vue"),
    "email-history-status": () =>
      import("@/components/app/global/emailHistory/emailHistoryStatus.vue")
  },
  computed: {
    ...mapState({
      client(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: this.clientDataModule,
          scope: this.clientScope
        });
      },
      clientScope(state: IState, getters) {
        return getters[`data/${this.clientDataModule}/scope`](this.clientId);
      },
      clientEmails(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.emailsDataModule,
          scope: this.emailsScope
        });
      },
      emailsScope(state: IState, getters) {
        return getters[`data/${this.emailsDataModule}/clientScope`](
          this.clientId
        );
      },
      params(state: IState, getters) {
        return getters[`data/${this.emailsDataModule}/params`](this.clientId);
      }
    })
  }
})
export default class ClientLogin extends Mixins(
  RequestPromises,
  HasValidation,
  LoggedUserMixin,
  Context
) {
  // :getters
  public client!: IClient;
  public clientScope!: string;
  public clientEmails!: IEmail[];
  public emailsScope!: string;
  public params!: object;

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ default: true })
  public isCollapse!: boolean;

  public clientDataModule: string = DataModules.CLIENTS;
  public emailsDataModule: string = DataModules.CLIENTS_EMAILS;

  public form = {
    email: "",
    has_login: true
  } as Form;
  public formClone = {} as Form;

  public isLoading = false;
  public isReloading = false;
  public requestError = false;

  public prevHasLogin = false;
  public isProcessing: object = {};

  public created() {
    if (this.isClient || this.$userCan("get_client")) {
      this.isLoading = true;
      this.loadData();
    }
  }

  public async loadData() {
    try {
      await this.getClientData(this.isReloading);
      await this.getClientEmailsData(this.isReloading);

      this.refreshClientForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }

    this.isReloading = false;
    this.isLoading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getClientData(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${this.clientDataModule}/getClientWithRelatedData`,
      {
        id: this.clientId,
        scope: this.clientScope,
        ignoreStored,
        vm: this
      }
    );
  }

  public getClientEmailsData(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.emailsDataModule}/list`, {
      id: this.clientId,
      params: {
        limit: 0,
        offset: 0,
        order: "-default,-id"
      },
      scope: this.emailsScope,
      ignoreStored,
      vm: this
    });
  }

  public refreshClientForm() {
    this.form = this.$_.merge({}, this.$_.pick(this.client, ["has_login"]), {
      email: this.$_.get(this.client, "email", "")
    });
    this.formClone = this.$_.clone(this.form);

    this.prevHasLogin = this.form.has_login;
  }

  public get isComponentProcessing() {
    return !this.$_.isEmpty(this.isProcessing);
  }

  public get formIsValid() {
    return !this.$_.isEqual(this.form, this.formClone);
  }

  public async confirmPassword() {
    const confirmPassword = await this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () => import("@/components/base/ConfirmPasswordModal.vue"),
        width: 480,
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.confirm"),
          message: this.$t("_sentence.change_login_email_confirmation", {
            object: (this.$t("_.action") as string).toLowerCase()
          }),
          action: this.$t("_action.confirm")
        },
        canCancel: ["button"],
        events: {
          confirmed: async (currentPassword: string) => {
            if (currentPassword) {
              await this.updateClientLoginDetails({
                email: this.form.email,
                password: currentPassword,
                has_login: true
              });
              if (
                this.isClient &&
                (this.loggedUser.id as any) === this.client.id
              ) {
                await this.$store.dispatch("auth/client/getUser");
              }
            }
            confirmPassword.close();
          },
          close: () => {
            this.form.email = this.client.email;
          }
        }
      }
    });
  }

  public async sendResetInstruction() {
    this.$set(this.isProcessing, "sendResetInstruction", true);

    try {
      await this.$store.dispatch(
        `data/${this.clientDataModule}/resetPassword`,
        { id: this.clientId }
      );

      this.$buefy.toast.open({
        message: this.$t("_.reset_password_email_sent") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
    this.$delete(this.isProcessing, "sendResetInstruction");
  }

  public openSetPasswordModal() {
    this.$store.dispatch(`data/${DataModules.CLIENTS}/openSetPasswordModal`, {
      props: {
        clientId: this.client.id
      }
    });
  }

  public get currentClientEmail() {
    return this.$_.find(this.clientEmails, (emailObj: any) => {
      return emailObj.email === this.form.email;
    });
  }

  public onHasLoginChange(hasLogin: string) {
    if (
      (!hasLogin || (hasLogin && !!this.form.email)) &&
      this.$userCan("update_client")
    ) {
      this.updateClientLoginDetails({
        has_login: this.form.has_login
      });
    }
  }

  public async updateClientLoginDetails(data) {
    this.$set(
      this.isProcessing,
      this.$_.get(data, "has_login") !== undefined
        ? "updateHasLogin"
        : "updateEmail",
      true
    );

    try {
      await this.updateClientLogin(data);
      await this.getClientData(true);

      this.refreshClientForm();

      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_updated", {
          object: this.$t("_.login_details")
        }) as string
      });
    } catch (e) {
      const error = e as AxiosError;
      if (error.response?.data?.error?.data?.password) {
        this.$_.set(
          error,
          "response.data.error.message",
          `${error.response.data.error.data.password[0]} ${this.$t(
            "_sentence.try_again"
          )}`
        );
      }

      this.form.email = this.client.email;
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }

    this.$delete(
      this.isProcessing,
      this.$_.get(data, "has_login") !== undefined
        ? "updateHasLogin"
        : "updateEmail"
    );
  }

  public updateClientLogin(data) {
    return this.$store.dispatch(
      `data/${this.clientDataModule}/updateLoginDetails`,
      {
        id: this.clientId,
        data
      }
    );
  }

  @Watch("form.email")
  public async onEmailChange(newEmail: string) {
    if (
      this.$_.isEmpty(newEmail) ||
      newEmail === this.client.email ||
      (this.isAdmin && !this.$userCan("create_client_email"))
    ) {
      return;
    }
    if (this.isAdmin) {
      await this.updateClientLoginDetails({
        email: this.form.email,
        has_login: true
      });
      await this.refreshEmailList();
      return;
    }

    await this.confirmPassword();
  }

  public async refreshEmailList() {
    if (!this.$_.isEmpty(this.params)) {
      await this.$store.dispatch(`data/${this.emailsDataModule}/list`, {
        id: this.clientId,
        params: this.params,
        scope: this.emailsScope,
        ignoreStored: true,
        vm: this
      });
    }
  }

  public createEmail() {
    this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import("@/components/app/global/client/addEditClientEmailsModal.vue"),
        props: {
          clientId: this.clientId,
          setNewLoginEmail: true
        },
        width: 540,
        events: {
          success: (result: IEmail) => {
            this.updateClientLoginDetails({
              email: result.email,
              has_login: true
            });
          }
        }
      }
    });
  }

  public get isChosenEmailBounced() {
    return this.$_.get(this.currentClientEmail, "bounced", false);
  }

  public get bouncedAtTime() {
    return this.$_.get(this.currentClientEmail, "bounced_at", null);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: this.clientDataModule,
      scope: this.clientScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: this.emailsDataModule,
      scope: this.emailsScope,
      vm: this
    });
  }
}
